import React, { useCallback, useEffect, useState } from 'react';
import {
  DefaultButton,
  Panel,
  PanelType,
  Stack,
  Label,
  StackItem,
  TextField,
  Text,
  ComboBox,
  IComboBoxOption,
  IComboBox,
  Toggle,
  Dropdown,
  Link,
  Icon,
  IDropdownOption,
  IStackItemStyles,
  getTheme,
  PrimaryButton,
  CommandBar,
  ShimmeredDetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  Selection,
} from '@fluentui/react';
import _, { unescape } from 'lodash';
import moment from 'moment';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { pdf } from '@react-pdf/renderer';
import {
  ADD_PURCHASE_ORDER,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_LOADING_MESSAGE,
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOAST_POSITION,
  GET_PURCHASE_ORDER_BY_ID,
  GET_STORAGE_TOKEN,
  IConstructionSite,
  IMeasuringUnit,
  IPurchaseOrder,
  IPurchaseOrderLine,
  IPurchaseOrderLineCreate,
  IPurchaseOrderLineUpdate,
  IPurchaseOrderStatus,
  IPurchaseOrderTemplate,
  ISupplier,
  UPDATE_PURCHASE_ORDER,
  convertConstructionSitesToComboBoxOptions,
  convertContactPersonsToComboBoxOptions,
  convertMeasuringUnitsToComboBoxOptions,
  convertPurchaseOrderTemplatesToComboBoxOptions,
  convertSuppliersToComboBoxOptions,
  dialogModelProps,
  iconLastProps,
  modalContentStyles,
  normalizeFileNameBestelbon,
  stackTokens5,
} from '../../../utils';
import SaveButtonWithPermissions from '../../../components/parts/SaveButtonWithPermissions';
import { useAppDispatch } from '../../../redux/hooks';
import { throwError, SeverityLevel } from '../../../redux/error/errorSlice';
import { sendNotification } from '../../../redux/notification/notificationSlice';
import parseNumber from '../../../utils/Numbers';
import {
  IPurchaseOrderLineDelete,
  getDefaultLineItem,
} from '../../../utils/PurchaseOrderLine';
import {
  ComboboxWithFilter,
  InlineEditableLabel,
} from '../../../components/parts';
import DraggableList from '../../../components/parts/DraggableList';
import { toastSuccess, toastError } from '../../../utils/toast';
import { commandBarTheme, customPanelStyles } from '../../../theme';
import { IMailLog } from '../../../utils/MailLog';
import { BlobStorage } from '../../blob-storage';
import { sendMailRequest, MailSendFailure } from '../../../utils/sendMail';
import AttachmentList from '../../attachments/AttachmentList';
import PurchaseOrderBillitLinks from './PurchaseOrderBillitLinks';
import PurchaseOrderFooter from './PurchaseOrderFooter';

import MailDetail from './MailDetail';
import PurchaseOrderTypeSwitch from './PurchaseOrderTypeSwitch';
import {
  CardGrid,
  getContactInfo,
  InfoBox,
  InfoMuted,
  PurchaseOrderMeta,
  SectionCard,
  SiteRemarksCallout,
} from './PurchaseOrderPanelParts';
import PurchaseOrderDownloadTemplate from './PurchaseOrderDownloadTemplate';

const theme = getTheme();
const styleTitle: IStackItemStyles = {
  root: {
    background: theme.palette.neutralLight,
    padding: '10px',
    margin: '0 24px 24px 24px',
    marginTop: '15px',
    marginBottom: '15px',
    paddingRight: '0px',
    alignItems: 'center',
    color: 'black',
    fontSize: '16px',
    fontWeight: 'bold',
    h3: {
      margin: 0,
    },
  },
};

const styleTitleBijlagen: IStackItemStyles = {
  root: {
    background: theme.palette.neutralLight,
    padding: '10px',
    marginTop: '15px',
    marginBottom: '15px',
    paddingRight: '0px',
    alignItems: 'center',
    color: 'black',
    fontSize: '16px',
    fontWeight: 'bold',
    h3: {
      margin: 0,
    },
  },
};

const addEmptyOrderLine = (purchaseOrder: IPurchaseOrder) => {
  const newPurchaseOrder = _.cloneDeep(purchaseOrder);
  const purchaseOrderLines = newPurchaseOrder.line_items || [];

  if (!newPurchaseOrder.line_items) {
    newPurchaseOrder.line_items = [];
  }
  if (newPurchaseOrder && newPurchaseOrder.line_items.length === 0) {
    newPurchaseOrder.line_items.push(getDefaultLineItem({}));
  } else if (newPurchaseOrder.line_items) {
    const arrayLength = newPurchaseOrder.line_items.length;
    const lastItem = newPurchaseOrder.line_items[arrayLength - 1];
    if ((lastItem && !lastItem.isNew) || (lastItem.isNew && lastItem.isDirty)) {
      const newWeight = (lastItem.weight || 0) + 1;
      newPurchaseOrder.line_items.push(
        getDefaultLineItem({ weight: newWeight }),
      );
    }
  }

  newPurchaseOrder.line_items = purchaseOrderLines;
  return newPurchaseOrder;
};

export const sortLineItems = (data: IPurchaseOrder) => {
  const newPurchaseOrder = _.cloneDeep(data);
  if (newPurchaseOrder.line_items) {
    newPurchaseOrder.line_items.sort(
      (a, b) => (a.weight || 0) - (b.weight || 0),
    );
  } else {
    newPurchaseOrder.line_items = [];
  }

  for (let i = 0; i < newPurchaseOrder.line_items.length; i++) {
    const currentWeight = newPurchaseOrder.line_items[i].weight;
    newPurchaseOrder.line_items[i].weight = i + 1;
    if (currentWeight !== newPurchaseOrder.line_items[i].weight) {
      newPurchaseOrder.line_items[i].isDirty = true;
    }
  }

  return newPurchaseOrder;
};

type Props = {
  downloadPDF?: (id?: number) => void;
  isOpen: boolean;
  dismissPanel: () => void;
  purchaseOrderSource?: IPurchaseOrder;
  constructionSites?: IConstructionSite[];
  templates?: IPurchaseOrderTemplate[];
  suppliers?: ISupplier[];
  statuses?: IPurchaseOrderStatus[];
  measuringUnits?: IMeasuringUnit[];
  constructionSiteSearch?: string;
  setConstructionSiteSearch: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  supplierSearch?: string;
  setSupplierSearch: React.Dispatch<React.SetStateAction<string | undefined>>;
  refetch: any;
  setSelectionDetails: any;
  setPurchaseOrders?: React.Dispatch<React.SetStateAction<IPurchaseOrder[]>>;
  fast?: boolean;
};

const PurchaseOrderDetail: React.FC<Props> = ({
  downloadPDF,
  isOpen,
  dismissPanel,
  purchaseOrderSource,
  constructionSites = [],
  templates = [],
  suppliers = [],
  statuses = [],
  measuringUnits = [],
  constructionSiteSearch,
  supplierSearch,
  setConstructionSiteSearch,
  setSupplierSearch,
  refetch,
  setSelectionDetails,
  setPurchaseOrders,
  fast,
}) => {
  const dispatch = useAppDispatch();
  const [purchaseOrder, setPurchaseOrder] = useState<
    IPurchaseOrder | undefined
  >(purchaseOrderSource ? sortLineItems(purchaseOrderSource) : undefined);
  const [template, setTemplate] = useState<
    IPurchaseOrderTemplate | undefined
  >();
  const [isDraggable, setIsDraggable] = useState(false);
  const [isMailPanelOpen, setIsMailPanelOpen] = useState(false);
  const [mail, setMail] = useState<any>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sendingMail, setSendingMail] = useState(false);
  const [selectionMailDetails, setSelectionMailDetails] = useState<
    IMailLog | undefined
  >();
  const { getAccessTokenSilently, user } = useAuth0();

  const selectionMail = new Selection({
    onSelectionChanged: () => {
      getSelectionMailDetails();
    },
  });

  useEffect(() => {
    setPurchaseOrder(() => {
      if (purchaseOrderSource) {
        // Werfopmerkingen worden niet meer in de opmerkingen van de bon gezet;
        // ze komen als apart blok op de PDF (zie PurchaseOrderDownloadTemplate).
        return sortLineItems(purchaseOrderSource);
      }
    });
    setIsDraggable(false); // reset on data change
  }, [purchaseOrderSource]);

  useEffect(() => {
    const currentPurchaseOrder = _.cloneDeep(purchaseOrder || {});
    const newPurchaseOrder = addEmptyOrderLine(currentPurchaseOrder);

    if (
      JSON.stringify(currentPurchaseOrder) !== JSON.stringify(newPurchaseOrder)
    ) {
      setPurchaseOrder(newPurchaseOrder);
    }
  }, [purchaseOrder]);

  useQuery(GET_PURCHASE_ORDER_BY_ID, {
    variables: {
      where: {
        id: purchaseOrderSource?.id,
      },
    },
    skip: !purchaseOrderSource || !purchaseOrderSource.id,
    onCompleted: (x: any) => {
      if (x && x.findOnePurchaseOrder) {
        let purchaseOrder: IPurchaseOrder = x.findOnePurchaseOrder;
        purchaseOrder = sortLineItems(purchaseOrder);
        setPurchaseOrder(purchaseOrder);
      }
    },
  });

  // Queries
  const [addPurchaseOrder] = useMutation(ADD_PURCHASE_ORDER, {});
  const [updatePurchaseOrder] = useMutation(UPDATE_PURCHASE_ORDER, {});

  // Functions
  const changeTemplate = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    // index?: number | undefined,
    // value?: string | undefined,
  ) => {
    const selectedKey = +option!.key;

    if (selectedKey) {
      const selectedTemplate = templates.filter(
        (item: IPurchaseOrderTemplate) => item.id === +option!.key,
      )[0];

      setTemplate(selectedTemplate);

      const useSupplierDefaultContact =
        selectedTemplate.is_default_contact_person !== false ||
        !selectedTemplate.contact_person;

      const newPurchaseOrder: IPurchaseOrder = {
        remarks: selectedTemplate.remarks || '',
        // De naam van het sjabloon is de standaardnaam van de bestelbon
        // ("Van Herck - Uitgraving"). De sjabloonnamen dragen die conventie al,
        // dus we plakken er geen leveranciersnaam meer voor. Het veld
        // "Omschrijving (intern)" blijft gewoon aanpasbaar; kiest de gebruiker een
        // ander sjabloon, dan wordt de naam opnieuw gezet.
        //
        // Voordien werd hier `selectedTemplate.description` gebruikt, maar dat
        // veld staat bij alle sjablonen leeg — er kwam dus nooit iets in te staan.
        description: selectedTemplate.name || purchaseOrder?.description,
        supplier: selectedTemplate.supplier,
        construction_site: purchaseOrder?.construction_site,
        creation_date: moment().toISOString(),
        delivery_to_other_supplier: false,
        is_on_call: false,
        is_purchase_order: true,
        // `is_default_contact_person` betekent "neem de standaard van de
        // leverancier". Staat hij op false, dan is er in het sjabloon bewust een
        // specifieke contactpersoon gekozen en die moet mee. De voorwaarde stond
        // hier omgekeerd, waardoor juist die gekozen persoon werd weggegooid en
        // de bestelbon terugviel op de eerste contactpersoon van de leverancier.
        // Zonder opgeslagen persoon vallen we alsnog terug op de standaard.
        is_default_contact_person: useSupplierDefaultContact,
        line_items: [],
        contact_person: useSupplierDefaultContact
          ? undefined
          : selectedTemplate.contact_person,
        status: {
          id: 1,
          label: 'Nieuw', // hard coded -> to change @TP
        },
      };

      if (selectedTemplate && selectedTemplate.line_items) {
        const templateLineItems = _.cloneDeep(selectedTemplate.line_items);

        const purchaseOrderLines: IPurchaseOrderLine[] = templateLineItems
          .sort((a, b) => (a.weight || 0) - (b.weight || 0))
          .map((item: IPurchaseOrderLine, index) => ({
            isNew: true,
            isDirty: true,
            id: -1 - index,
            weight: index + 1,
            quantity: item.quantity,
            unit_price: item.unit_price,
            comment: item.comment,
            name: item.name + (item.description ? ` ${item.description}` : ''),
            measuring_unit:
              item.measuring_unit && item.measuring_unit.id
                ? {
                    // probably make this optional? Or store in template?
                    id: item.measuring_unit.id,
                    name: item.measuring_unit.name,
                  }
                : {
                    // probably make this optional? Or store in template?
                    id: 9,
                    name: 'GEEN',
                  },
          }));

        if (newPurchaseOrder.line_items) {
          newPurchaseOrder.line_items = purchaseOrderLines;
        }
      }
      setPurchaseOrder({
        ...purchaseOrder,
        ...newPurchaseOrder,
      });
    }
  };

  const setSupplier = useCallback(
    (newValue: IComboBoxOption) => {
      if (suppliers && suppliers.length > 0 && newValue) {
        const index = suppliers.findIndex(
          (item: ISupplier) => item.id === newValue.key,
        );
        const supplier = suppliers[index];
        if (supplier) {
          setPurchaseOrder(prevState => ({
            ...prevState,
            supplier,
          }));
        }
      }
    },
    [purchaseOrder, suppliers],
  );

  const setConstructionSite = useCallback(
    (newValue: IComboBoxOption) => {
      if (constructionSites && constructionSites.length > 0 && newValue) {
        const index = constructionSites.findIndex(
          (item: IConstructionSite) => item.id === newValue.key,
        );

        const constructionSite = constructionSites[index];
        if (constructionSite) {
          setPurchaseOrder(prevState => ({
            ...prevState,
            construction_site: constructionSite,
          }));
        }
      }
    },
    [purchaseOrder, constructionSites],
  );

  // Save and update area
  const getContactPersonValues = (order: IPurchaseOrder) => {
    let result = {};

    if (order.is_default_contact_person) {
      result = {
        ...result,
        is_default_contact_person: true,
      };
    } else if (order.contact_person && order.contact_person.id) {
      result = {
        ...result,
        is_default_contact_person: false,
        contact_person: {
          connect: {
            id: order.contact_person.id,
          },
        },
      };
    }

    return result;
  };

  const getRemarksValues = (order: IPurchaseOrder) => {
    if (order.remarks) return { remarks: order.remarks };
    return { remarks: '' };
  };

  const getDeliveryValues = (order: IPurchaseOrder) => {
    let result = {};

    if (order.delivery_to_other_supplier) {
      if (order.deliver_to_supplier && order.deliver_to_supplier.id) {
        result = {
          ...result,
          deliver_to_supplier: {
            connect: {
              id: order.deliver_to_supplier.id,
            },
          },
        };
      }
    }
    if (order.construction_site && order.construction_site.id) {
      result = {
        ...result,
        construction_site: {
          connect: {
            id: order.construction_site.id,
          },
        },
      };
    }

    return result;
  };

  const createPurchaseOrder = useCallback(async () => {
    if (!purchaseOrder) return;

    try {
      if (purchaseOrder) {
        const res = await toast.promise(
          new Promise((resolve, reject) => {
            let createValues: any = {
              delivery_date: purchaseOrder.delivery_date,
              description: purchaseOrder.description,
              is_on_call: purchaseOrder.is_on_call,
              pick_up: purchaseOrder.pick_up,
              no_delivery_hour: purchaseOrder.no_delivery_hour,
              on_call_value: purchaseOrder.on_call_value,
              delivery_to_other_supplier:
                purchaseOrder.delivery_to_other_supplier,
              is_purchase_order: purchaseOrder.is_purchase_order ?? true, // zelfde standaard als de database
              construction_site: purchaseOrder.construction_site
                ? {
                    connect: {
                      id: purchaseOrder.construction_site.id,
                    },
                  }
                : undefined,
              supplier:
                purchaseOrder &&
                purchaseOrder.supplier &&
                purchaseOrder.supplier.id
                  ? {
                      connect: {
                        id: purchaseOrder.supplier.id,
                      },
                    }
                  : undefined,
              status: purchaseOrder.status
                ? {
                    connect: {
                      id: purchaseOrder.status.id,
                    },
                  }
                : undefined,
            };

            const contactPersonValues = getContactPersonValues(purchaseOrder);
            const remarksValues = getRemarksValues(purchaseOrder);
            const deliveryValues = getDeliveryValues(purchaseOrder);

            if (
              purchaseOrder &&
              purchaseOrder.construction_site &&
              purchaseOrder.construction_site.id
            ) {
              createValues = {
                ...createValues,
                construction_site: {
                  connect: {
                    id: purchaseOrder.construction_site.id,
                  },
                },
              };
            }

            if (
              purchaseOrder.line_items &&
              purchaseOrder.line_items.length > 0
            ) {
              const created: IPurchaseOrderLineCreate[] = [];
              purchaseOrder.line_items.forEach((item: IPurchaseOrderLine) => {
                if (item.isNew && item.isDirty && !item.delete) {
                  created.push({
                    name: item.name,
                    comment: item.comment ? item.comment : undefined,
                    quantity: parseNumber(item.quantity),
                    unit_price: parseNumber(item.unit_price),
                    highlighted: item.highlighted || false,
                    measuring_unit: {
                      connect: {
                        id: item.measuring_unit.id,
                      },
                    },
                    weight: item.weight,
                  });
                }
              });

              let lineValues = {};

              if (created.length > 0) {
                lineValues = {
                  ...lineValues,
                  create: created,
                };
              }

              createValues = {
                ...createValues,
                line_items: {
                  ...lineValues,
                },
              };
            }

            addPurchaseOrder({
              variables: {
                data: {
                  ...createValues,
                  ...contactPersonValues,
                  ...remarksValues,
                  ...deliveryValues,
                },
              },
              onCompleted: async (x: any) => {
                refetch();
                resolve(x);
                //  setPurchaseOrder(x.data.findManyPurchaseOrders);
              },
              onError: (error: any) => {
                reject(error);
              },
            });
          }),
          {
            pending: {
              position: DEFAULT_TOAST_POSITION,
              render() {
                return DEFAULT_LOADING_MESSAGE;
              },
            },
          },
          {
            autoClose: DEFAULT_TOAST_DURATION,
          },
        );

        const x = await (res as any);
        if (x.createPurchaseOrder) {
          let newPurchaseOrder = x.createPurchaseOrder;
          newPurchaseOrder = sortLineItems(newPurchaseOrder);
          setSelectionDetails(newPurchaseOrder);
          setPurchaseOrder(newPurchaseOrder);

          toastSuccess('Inkooporder opgeslagen');
        }
      }
    } catch (error: any) {
      toastError(error.message ? error.message : DEFAULT_ERROR_MESSAGE);
    }
  }, [purchaseOrder]);

  const modifyPurchaseOrder = useCallback(async () => {
    if (!purchaseOrder) {
      return;
    }
    try {
      if (purchaseOrder) {
        const res = await toast.promise(
          new Promise((resolve, reject) => {
            let updatedValues = {};

            updatedValues = {
              ...updatedValues,
              description: purchaseOrder.description,
              delivery_date: purchaseOrder.delivery_date,
              is_on_call: purchaseOrder.is_on_call,
              pick_up: purchaseOrder.pick_up,
              no_delivery_hour: purchaseOrder.no_delivery_hour,
              on_call_value: purchaseOrder.on_call_value,
              delivery_to_other_supplier:
                purchaseOrder.delivery_to_other_supplier,
              is_purchase_order: purchaseOrder.is_purchase_order,
              construction_site: purchaseOrder.construction_site
                ? {
                    connect: {
                      id: purchaseOrder.construction_site.id,
                    },
                  }
                : undefined,
              supplier:
                purchaseOrder &&
                purchaseOrder.supplier &&
                purchaseOrder.supplier.id
                  ? {
                      connect: {
                        id: purchaseOrder.supplier.id,
                      },
                    }
                  : undefined,
              status: purchaseOrder.status
                ? {
                    connect: {
                      id: purchaseOrder.status.id,
                    },
                  }
                : undefined,
            };

            const contactPersonValues = getContactPersonValues(purchaseOrder);
            const remarksValues = getRemarksValues(purchaseOrder);
            const deliveryValues = getDeliveryValues(purchaseOrder);

            if (
              purchaseOrder.line_items &&
              purchaseOrder.line_items.length > 0
            ) {
              const created: IPurchaseOrderLineCreate[] = [];
              const updated: IPurchaseOrderLineUpdate[] = [];
              const deleted: IPurchaseOrderLineDelete[] = [];

              purchaseOrder.line_items.forEach((item: IPurchaseOrderLine) => {
                if (item.isNew && !item.delete && item.isDirty) {
                  let lineItem = {};
                  if (item && item.id && item.id > 0) {
                    lineItem = {
                      connect: {
                        id: item.id,
                      },
                    };
                  } else {
                    lineItem = {
                      create: {
                        name: item.name,
                        sku: 'x',
                      },
                    };
                  }

                  created.push({
                    name: item.name,
                    comment: item.comment ? item.comment : undefined,
                    quantity: parseNumber(item.quantity),
                    unit_price: parseNumber(item.unit_price),
                    highlighted: item.highlighted || false,
                    measuring_unit: {
                      connect: {
                        id: item.measuring_unit.id,
                      },
                    },
                    weight: item.weight,
                  });
                } else if (item.isDirty && !item.delete) {
                  updated.push({
                    data: {
                      name: item.name,
                      comment: item.comment ? item.comment : '',
                      quantity: parseNumber(item.quantity),
                      unit_price: parseNumber(item.unit_price),
                      highlighted: item.highlighted || false,
                      measuring_unit: {
                        connect: {
                          id: item.measuring_unit.id,
                        },
                      },
                      weight: item.weight,
                    },
                    where: {
                      id: item.id,
                    },
                  });
                } else if (item.delete && !item.isNew) {
                  deleted.push({
                    id: item.id,
                  });
                }
              });

              let lineValues = {};

              if (created.length > 0) {
                lineValues = {
                  ...lineValues,
                  create: created,
                };
              }

              if (updated.length > 0) {
                lineValues = {
                  ...lineValues,
                  update: updated,
                };
              }

              if (deleted.length > 0) {
                lineValues = {
                  ...lineValues,
                  delete: deleted,
                };
              }

              updatedValues = {
                ...updatedValues,
                line_items: {
                  ...lineValues,
                },
              };
            }

            updatePurchaseOrder({
              variables: {
                id: purchaseOrder.id,
                data: {
                  ...updatedValues,
                  ...contactPersonValues,
                  ...remarksValues,
                  ...deliveryValues,
                },
              },
              onCompleted: (x: any) => {
                resolve(x);
              },
              onError: (error: any) => {
                reject(error);
              },
            });
          }),
          {
            pending: {
              position: DEFAULT_TOAST_POSITION,
              render() {
                return DEFAULT_LOADING_MESSAGE;
              },
            },
          },
          {
            autoClose: DEFAULT_TOAST_DURATION,
          },
        );

        const x = await (res as any);
        if (x && x.updatePurchaseOrder) {
          setPurchaseOrder(prevState => {
            let newPurchaseOrder = x.updatePurchaseOrder;
            newPurchaseOrder = sortLineItems(newPurchaseOrder);

            return newPurchaseOrder;
          });
          toastSuccess('Inkooporder opgeslagen');
        }
      }
    } catch (error: any) {
      toastError(error.message ? error.message : DEFAULT_ERROR_MESSAGE);
    }
  }, [purchaseOrder]);

  const savePurchaseOrder = useCallback(async () => {
    if (purchaseOrder) {
      if (purchaseOrder && purchaseOrder.id && purchaseOrder.id > 0) {
        await modifyPurchaseOrder();
      } else {
        await createPurchaseOrder();
      }
    }
  }, [purchaseOrder]);

  const onChangeTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    setPurchaseOrder((prevState: any) => ({
      ...prevState,
      [(event.target as HTMLTextAreaElement).name]: newValue || '',
    }));
  };

  const onDescriptionChange = useCallback(
    (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string | undefined,
    ) => {
      const newOrder: IPurchaseOrder = purchaseOrder
        ? _.cloneDeep(purchaseOrder)
        : {};

      newOrder.description = newValue;

      setPurchaseOrder(newOrder);
    },
    [purchaseOrder],
  );

  const onChangeIsPurchaseOrder = useCallback(
    (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
      const newOrder: IPurchaseOrder = purchaseOrder
        ? _.cloneDeep(purchaseOrder)
        : {};
      newOrder.is_purchase_order = checked ?? false;

      setPurchaseOrder(newOrder);
    },
    [purchaseOrder],
  );

  const getTemplatesIncludingNewOption = useCallback(() => {
    const newOption = {
      key: 0,
      text: 'Nieuw',
    };

    return [
      newOption,
      ...convertPurchaseOrderTemplatesToComboBoxOptions(templates),
    ];
  }, [templates]);

  const onStatusChange = (
    ev: React.MouseEvent<HTMLElement>,
    checked?: boolean,
  ) => {
    const newOrder: IPurchaseOrder = purchaseOrder
      ? _.cloneDeep(purchaseOrder)
      : {};
    const statusID = checked ? 2 : 1;
    if (newOrder && statuses) {
      const selectedStatus = statuses.filter(status => {
        if (status.id === statusID) return true;
        return false;
      })[0];
      newOrder.status = selectedStatus;
      setPurchaseOrder(newOrder);
    }
  };

  // combine suppliers with purchaseOrder.suppliers if it doesnt exist in the array
  const getSuppliersIncludingPurchaseOrderSuppliers = useCallback(() => {
    if (purchaseOrder && purchaseOrder.supplier && suppliers) {
      const supplierArray = _.cloneDeep(suppliers);
      const supplier = supplierArray.find(
        (e: ISupplier) => e.id === purchaseOrder.supplier?.id,
      );
      if (!supplier) {
        supplierArray.push(purchaseOrder.supplier);
      }

      supplierArray.sort((obj1: ISupplier, obj2: ISupplier) => {
        let compareName = 0;
        compareName = obj1.name.localeCompare(obj2.name);

        return compareName;
      });

      return supplierArray;
    }
    if (suppliers) {
      const supplierArray = _.cloneDeep(suppliers);
      supplierArray.sort((obj1: ISupplier, obj2: ISupplier) => {
        let compareName = 0;
        compareName = obj1.name.localeCompare(obj2.name);

        return compareName;
      });

      return supplierArray;
    }
    return [];
  }, [suppliers, purchaseOrder]);

  const getConstructionSitesIncludingPurchaseOrderConstructionSite =
    useCallback(() => {
      if (
        purchaseOrder &&
        purchaseOrder.construction_site &&
        constructionSites
      ) {
        const constructionSiteArray = _.cloneDeep(constructionSites);
        const constructionSite = constructionSiteArray.find(
          (e: IConstructionSite) =>
            e.id === purchaseOrder.construction_site?.id,
        );
        if (!constructionSite) {
          constructionSiteArray.push(purchaseOrder.construction_site);
        }

        constructionSiteArray.sort(
          (obj1: IConstructionSite, obj2: IConstructionSite) => {
            let compareName = 0;
            compareName = obj1.name.localeCompare(obj2.name);

            return compareName;
          },
        );

        return constructionSiteArray;
      }
      if (constructionSites) {
        const constructionSiteArray = _.cloneDeep(constructionSites);
        constructionSiteArray.sort(
          (obj1: IConstructionSite, obj2: IConstructionSite) => {
            let compareName = 0;
            compareName = obj1.name.localeCompare(obj2.name);

            return compareName;
          },
        );

        return constructionSiteArray;
      }
      return [];
    }, [suppliers, purchaseOrder, constructionSites]);

  const getContactLabel = useCallback(() => {
    if (
      purchaseOrder &&
      purchaseOrder.supplier &&
      purchaseOrder.supplier.contact_persons &&
      purchaseOrder.is_default_contact_person
    ) {
      return (
        <Label>
          {purchaseOrder.supplier.contact_persons[0]?.first_name}{' '}
          {purchaseOrder.supplier.contact_persons[0]?.last_name}
        </Label>
      );
    }
    if (
      purchaseOrder &&
      purchaseOrder.supplier &&
      purchaseOrder.contact_person &&
      !purchaseOrder.is_default_contact_person
    ) {
      return (
        <Label>
          {purchaseOrder.contact_person.first_name}{' '}
          {purchaseOrder.contact_person.last_name}
        </Label>
      );
    }

    return <Label />;
  }, [purchaseOrder]);

  const getPhoneLabel = useCallback(() => {
    const selectedSupplier =
      purchaseOrder && purchaseOrder.supplier
        ? purchaseOrder.supplier
        : undefined;
    const supplierContactPhone =
      selectedSupplier &&
      selectedSupplier.contact_persons &&
      selectedSupplier.contact_persons[0] &&
      (selectedSupplier.contact_persons[0].phone_v2 ||
        selectedSupplier.contact_persons[0].mobile_v2 ||
        selectedSupplier.contact_persons[0].phone ||
        selectedSupplier.contact_persons[0].mobile)
        ? selectedSupplier.contact_persons[0].phone_v2 ||
          selectedSupplier.contact_persons[0].mobile_v2 ||
          selectedSupplier.contact_persons[0].phone ||
          selectedSupplier.contact_persons[0].mobile
        : undefined;
    return purchaseOrder && purchaseOrder.contact_person ? (
      <Label>
        {purchaseOrder.contact_person.phone_v2 ||
          purchaseOrder.contact_person.mobile_v2 ||
          purchaseOrder.contact_person.phone ||
          purchaseOrder.contact_person.mobile}
      </Label>
    ) : supplierContactPhone ? (
      <Label>{supplierContactPhone}</Label>
    ) : null;
  }, [purchaseOrder]);

  const getFaxLabel = useCallback(() => {
    const selectedSupplier =
      purchaseOrder && purchaseOrder.supplier
        ? purchaseOrder.supplier
        : undefined;
    const supplierContactFax =
      selectedSupplier &&
      selectedSupplier.contact_persons &&
      selectedSupplier.contact_persons[0] &&
      selectedSupplier.contact_persons[0].fax
        ? selectedSupplier.contact_persons[0].fax
        : undefined;
    return purchaseOrder && purchaseOrder.contact_person ? (
      // eslint-disable-next-line react/jsx-indent
      <Label>{purchaseOrder.contact_person.fax}</Label>
    ) : supplierContactFax ? (
      <Label>{supplierContactFax}</Label>
    ) : null;
  }, [purchaseOrder]);

  const loadContactPersons = useCallback(() => {
    const supplier =
      suppliers.find(item => item.id === purchaseOrder?.supplier?.id) ||
      purchaseOrder?.supplier;
    if (supplier) {
      let contactArray: IComboBoxOption[] = [];

      const contactArrayTemp = supplier.contact_persons || [];

      const comboBoxOptions =
        convertContactPersonsToComboBoxOptions(contactArrayTemp);

      if (comboBoxOptions && comboBoxOptions.length > 0) {
        contactArray = contactArray.concat(comboBoxOptions);
      }

      return contactArray;
    }
    return [];
  }, [purchaseOrder, suppliers]);

  const setContactPerson = useCallback(
    (value: IComboBoxOption) => {
      const newOrder: IPurchaseOrder = purchaseOrder
        ? _.cloneDeep(purchaseOrder)
        : {};
      if (newOrder && value) {
        const selectedContact = newOrder.supplier?.contact_persons?.filter(
          contact => {
            if (contact.id === value.key) return true;
            return false;
          },
        )[0];
        newOrder.contact_person = selectedContact;
        newOrder.is_default_contact_person = false;
        setPurchaseOrder(newOrder);
      }
    },
    [purchaseOrder],
  );

  const updateItemValue = (item: any, attribute: string, newValue: string) => {
    // const currentOrder = _.cloneDeep(purchaseOrder);
    const newItem = { ...item };

    newItem[attribute] = newValue;

    newItem.isDirty = true;
    newItem.isEdit = true;

    updateItems(newItem);
  };

  const updateItemMeasuringUnit = (
    item: IPurchaseOrderLine,
    newUnit?: IDropdownOption,
  ) => {
    if (measuringUnits && newUnit) {
      const newMeasuringUnit = measuringUnits.filter(
        (item: IMeasuringUnit) => item.id === newUnit.key,
      )[0];
      const newItem = { ...item };

      newItem.measuring_unit = newMeasuringUnit;
      newItem.isDirty = true;

      updateItems(newItem);
    }
  };

  const updateItemHighlighted = (
    item: IPurchaseOrderLine,
    checked?: boolean,
  ) => {
    const newItem = { ...item };
    newItem.highlighted = checked ?? false;
    newItem.isDirty = true;

    updateItems(newItem);
  };

  const updateItems = (item: IPurchaseOrderLine) => {
    const currentOrder = _.cloneDeep(purchaseOrder);
    const newOrderLines = currentOrder ? currentOrder.line_items : [];

    if (newOrderLines) {
      for (let i = 0; i < newOrderLines.length; i++) {
        if (newOrderLines[i].id === item.id) {
          newOrderLines[i] = item;
        }
      }
    }

    if (newOrderLines && currentOrder) {
      currentOrder.line_items = newOrderLines;

      setPurchaseOrder(currentOrder);
    }
  };

  const toggleDeleteItem = (id: number) => {
    const currentOrder = _.cloneDeep(purchaseOrder || {});
    const currentOrderLines: IPurchaseOrderLine[] =
      currentOrder && currentOrder.line_items ? currentOrder.line_items : [];

    for (let i = 0; i < currentOrderLines.length; i++) {
      if (currentOrderLines[i].id === id) {
        currentOrderLines[i].delete = !currentOrderLines[i].delete;
        currentOrderLines[i].isDirty = true;
      }
    }

    currentOrder.line_items = currentOrderLines;
    setPurchaseOrder(currentOrder);
  };

  const orderLineItems = (source: number, target: number) => {
    const currentOrder = _.cloneDeep(purchaseOrder || {});
    const currentOrderLines: IPurchaseOrderLine[] =
      currentOrder && currentOrder.line_items ? currentOrder.line_items : [];

    const fromIndex = source;
    const toIndex = target;

    const element = currentOrderLines.splice(fromIndex, 1)[0];

    currentOrderLines.splice(toIndex, 0, element);

    for (let i = 0; i < currentOrderLines.length; i++) {
      currentOrderLines[i].weight = i + 1;
      if (!currentOrderLines[i].isNew) {
        currentOrderLines[i].isDirty = true;
      }
    }

    currentOrder.line_items = currentOrderLines;

    setPurchaseOrder(currentOrder);
  };

  const getSelectionMailDetails = () => {
    const currentMailSelection: any = selectionMail.getSelection();
    if (currentMailSelection.length > 0) {
      setSelectionMailDetails(currentMailSelection[0]);
    } else {
      setSelectionMailDetails(undefined);
    }
  };

  /**
   * Haalt de bijlagen op die op de bestelbon zelf bewaard zijn. Die staan al in
   * de opslag, dus ze hoeven niet opnieuw geüpload te worden — enkel doorgegeven.
   */
  const getPurchaseOrderAttachments = async () => {
    if (!purchaseOrder?.id) return [];
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/purchase-orders/${purchaseOrder.id}/attachments`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) return [];
      const bijlagen = await response.json();
      return (bijlagen || []).map((a: any) => ({
        name: a.blob_name,
        originalName: a.original_name,
        contentType: a.content_type || undefined,
        extension: String(a.original_name || '').split('.').pop()?.toLowerCase(),
        url: `https://3bouw.blob.core.windows.net/attachments/${a.blob_name}`,
      }));
    } catch {
      // Bijlagen ophalen mag het openen van het mailvenster niet blokkeren.
      return [];
    }
  };

  const openMailDetail = async (newMail?: boolean) => {
    if (selectionMailDetails && !newMail) {
      setMail(selectionMailDetails);
    } else {
      setSelectedFiles([]);
      const selected_supplier = purchaseOrder?.supplier;
      const selected_contact_person = purchaseOrder?.contact_person;

      // Zelfde volgorde als het scherm toont: de gekozen contactpersoon, anders
      // de eerste van de leverancier (dat is wat er bij "standaard contactpersoon"
      // op het scherm staat), en pas daarna het algemene adres. Voordien viel hij
      // meteen terug op het algemene adres zodra er geen eigen contactpersoon was,
      // waardoor de mail bij 21 leveranciers ergens anders belandde dan getoond.
      const contact_person_email = selected_contact_person?.id
        ? selected_supplier?.contact_persons?.find(
          (person: any) => person.id === selected_contact_person.id,
        )?.email
        : selected_supplier?.contact_persons?.[0]?.email;

      setMail({
        to:
          purchaseOrder?.supplier?.email || contact_person_email
            ? JSON.stringify([
                contact_person_email || purchaseOrder?.supplier?.email,
              ])
            : '',
        subject: `Bestelling ${purchaseOrder?.construction_site?.name}`,
        from: user?.email ? user.email : '',
        // De bijlagen van de bestelbon gaan standaard mee; ze zijn met de chips
        // in het mailvenster te verwijderen wanneer dat niet gewenst is.
        attachments: JSON.stringify(await getPurchaseOrderAttachments()),
      });
    }
    setIsMailPanelOpen(true);
  };

  // Bewust ZONDER onCompleted: fouten binnen die callback worden door Apollo
  // opgeslokt, waardoor een mislukte verzending nergens zichtbaar was.
  const [acquireToken] = useLazyQuery(GET_STORAGE_TOKEN, {
    fetchPolicy: 'no-cache',
  });

  /** Zet de geselecteerde bestanden in de opslag en geeft hun verwijzingen terug. */
  const uploadSelectedFiles = async (tokenData: any) => {
    const fileStore = new BlobStorage(tokenData.accountName, tokenData.sasToken);

    return Promise.all(
      selectedFiles.map(async file => {
        const fileName = `${Date.now()}_${file?.name}`;
        const buffer = await file.arrayBuffer();
        await fileStore.uploadBlob('attachments', fileName, buffer, buffer.byteLength);
        return {
          name: fileName,
          url: `https://${tokenData.accountName}.blob.core.windows.net/attachments/${fileName}`,
          extension: fileName.split('.').pop()?.toLowerCase(),
        };
      }),
    );
  };

  /** Bijlagen die al aan de mail hangen (die van de bestelbon, min de weggeklikte). */
  const getExistingMailAttachments = () => {
    try {
      const bestaand = JSON.parse(mail?.attachments || '[]');
      return Array.isArray(bestaand) ? bestaand : [];
    } catch {
      return [];
    }
  };

  const sendMail = async (attachments: any[]) => {
    const token = await getAccessTokenSilently();
    if (
      !token ||
      !user?.email ||
      !(user.email.includes('@3bouw') || user.email.includes('@ismart'))
    ) {
      throw new MailSendFailure('unknown', 'Gebruiker niet gevonden. De mail is NIET verstuurd.');
    }

    // Gooit wanneer er niets verstuurd is; alles hieronder draait dus enkel na
    // een bevestigde verzending.
    await sendMailRequest(token, {
      from: user.email,
      to: mail.to,
      cc: mail.cc,
      bcc: mail.bcc,
      subject: mail.subject,
      body: mail.body,
      attachments: JSON.stringify(attachments),
      construction_site_id: purchaseOrder?.construction_site?.id,
      purchase_order_id: purchaseOrder?.id,
    });

    // De mail IS hier verstuurd. Alles wat volgt werkt enkel het scherm bij;
    // een fout daarin mag NOOIT als verzendfout getoond worden, anders zou een
    // gebruiker denken dat er niets vertrokken is terwijl dat wel zo is.
    try {
      // Vanaf hier is de verzending bevestigd door de backend.
      const nextStatusId = purchaseOrder?.status?.id && purchaseOrder.status.id !== 1
        ? 3
        : 2;
      const sentStatus = statuses.find(status => status.id === nextStatusId) || {
        id: nextStatusId,
        label: nextStatusId === 3 ? 'Herstuurd' : 'Verstuurd',
      };

      setPurchaseOrder(prev => (prev ? { ...prev, status: sentStatus } : prev));
      setSelectionDetails?.((prev: IPurchaseOrder | undefined) => (
        prev && prev.id === purchaseOrder?.id
          ? { ...prev, status: sentStatus }
          : prev
      ));
      setPurchaseOrders?.(prev => prev.map(order => (
        order.id === purchaseOrder?.id
          ? { ...order, status: sentStatus }
          : order
      )));

      toastSuccess('Email verstuurd');
      setIsMailPanelOpen(false);
    } catch (uiError) {
      console.error('Mail verstuurd, maar het bijwerken van het scherm faalde:', uiError);
    }
  };

  const saveMail = async () => {
    if (sendingMail) return;
    setSendingMail(true);
    try {
      const { data } = await acquireToken();
      if (!data?.createStorageToken) {
        throw new MailSendFailure('unknown', 'Geen toegang tot de bestandsopslag. De mail is NIET verstuurd.');
      }

      const files = await uploadSelectedFiles(data.createStorageToken);
      await sendMail([...getExistingMailAttachments(), ...files]);

      setSelectedFiles([]);
    } catch (error) {
      // Doorgeven aan het mailvenster: dat toont de fout naast de Verzenden-knop,
      // waar de gebruiker kijkt. Een toast verdween achter het paneel.
      throw error instanceof MailSendFailure
        ? error
        : new MailSendFailure('unknown', 'Versturen van de mail is mislukt. De mail is NIET verstuurd.');
    } finally {
      setSendingMail(false);
      // Verversen is bijzaak en mag nooit gelezen worden als een verzendfout.
      Promise.resolve(refetch?.()).catch(() => undefined);
    }
  };

  const [getPurchaseOrder] = useLazyQuery(GET_PURCHASE_ORDER_BY_ID);

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel='Close'
      headerText={`${
        purchaseOrder?.is_purchase_order === false ? 'Prijsaanvraag' : 'Bestelbon'
      } ${purchaseOrder && purchaseOrder.id ? 'wijzigen' : 'toevoegen'}`}
      customWidth='1000px'
      type={PanelType.large}
      styles={customPanelStyles}
    >
      <div>
        <div className={modalContentStyles.header} />
        <PurchaseOrderMeta purchaseOrder={purchaseOrder} />
        <div style={{ padding: '0 24px 16px' }}>
          <SectionCard title='Algemeen' icon='Info'>
            <Stack horizontal wrap verticalAlign='start' tokens={{ childrenGap: 32 }}>
              <Stack.Item>
                <PurchaseOrderTypeSwitch
                  // nieuwe bon = bestelbon, zoals opgeslagen
                  isPurchaseOrder={purchaseOrder?.is_purchase_order !== false}
                  onChange={value => onChangeIsPurchaseOrder(undefined as any, value)}
                />
              </Stack.Item>
              {purchaseOrder && purchaseOrder.id && purchaseOrder.id > 0 ? null : (
                <Stack.Item styles={{ root: { minWidth: 220 } }}>
                  <ComboBox
                    selectedKey={template?.id || 0}
                    allowFreeform
                    autoComplete='on'
                    options={getTemplatesIncludingNewOption()}
                    onChange={changeTemplate}
                    label='Sjabloon'
                  />
                </Stack.Item>
              )}
              <Stack.Item>
                <Toggle
                  label='Status'
                  onText='Verstuurd'
                  offText='Nieuw'
                  checked={
                    !(
                      !purchaseOrder ||
                      !purchaseOrder.status ||
                      purchaseOrder.status.id === 1
                    )
                  }
                  onChange={onStatusChange}
                />
              </Stack.Item>
            </Stack>
            <TextField
              label='Omschrijving (intern)'
              styles={{ root: { marginTop: 12 } }}
              value={
                purchaseOrder && purchaseOrder.description
                  ? purchaseOrder.description
                  : ''
              }
              onChange={onDescriptionChange}
            />
          </SectionCard>
        </div>
        <CardGrid>
          <SectionCard title='Leverancier' icon='DeliveryTruck'>
            <ComboboxWithFilter
              label='Leverancier'
              options={convertSuppliersToComboBoxOptions(
                getSuppliersIncludingPurchaseOrderSuppliers(),
              )}
              value={
                purchaseOrder && purchaseOrder.supplier
                  ? purchaseOrder.supplier.id
                  : ''
              }
              multiline={false}
              callBack={(newValue: IComboBoxOption[]) => {
                if (newValue && newValue.length > 0) {
                  setSupplier(newValue[0]);
                }
              }}
              setFilter={filterValue => {
                setSupplierSearch(filterValue);
              }}
              allowFreeForm
              style={{ width: '100%' }}
            />
            <div style={{ height: 8 }} />
            <ComboboxWithFilter
              label='Contactpersoon'
              options={loadContactPersons()}
              value={
                purchaseOrder && purchaseOrder.contact_person
                  ? purchaseOrder.contact_person.id
                  : ''
              }
              multiline={false}
              callBack={(newValue: IComboBoxOption[]) => {
                if (newValue && newValue.length > 0) {
                  setContactPerson(newValue[0]);
                }
              }}
              style={{ width: '100%' }}
            />
            {purchaseOrder && purchaseOrder.supplier && purchaseOrder.supplier.id ? (
              <InfoBox
                title={purchaseOrder.supplier.name}
                lines={[
                  purchaseOrder.supplier.address,
                  [purchaseOrder.supplier.zip_code, purchaseOrder.supplier.city].filter(Boolean).join(' '),
                  purchaseOrder.supplier.email ? (
                    <Link href={`mailto:${purchaseOrder.supplier.email}`}>
                      {purchaseOrder.supplier.email}
                    </Link>
                  ) : null,
                  getContactInfo(purchaseOrder).name ? <InfoMuted>Contact: {getContactInfo(purchaseOrder).name}</InfoMuted> : null,
                  getContactInfo(purchaseOrder).phone ? <InfoMuted>Tel.: {getContactInfo(purchaseOrder).phone}</InfoMuted> : null,
                  getContactInfo(purchaseOrder).fax ? <InfoMuted>Fax: {getContactInfo(purchaseOrder).fax}</InfoMuted> : null,
                ]}
              />
            ) : null}
          </SectionCard>
          <SectionCard title='Werf' icon='CityNext'>
            <ComboboxWithFilter
              label='Werf'
              options={convertConstructionSitesToComboBoxOptions(
                getConstructionSitesIncludingPurchaseOrderConstructionSite(),
              )}
              value={
                purchaseOrder && purchaseOrder.construction_site
                  ? purchaseOrder.construction_site.id
                  : ''
              }
              multiline={false}
              callBack={(newValue: IComboBoxOption[]) => {
                if (newValue && newValue.length > 0) {
                  setConstructionSite(newValue[0]);
                }
              }}
              setFilter={filterValue => {
                setConstructionSiteSearch(filterValue);
              }}
              allowFreeForm
              style={{ width: '100%' }}
            />
            {purchaseOrder &&
            purchaseOrder.construction_site &&
            (purchaseOrder.construction_site.name ||
              purchaseOrder.construction_site.address) ? (
                <InfoBox
                  title={purchaseOrder.construction_site.name}
                  lines={[
                    purchaseOrder.construction_site.address,
                    [purchaseOrder.construction_site.zip_code, purchaseOrder.construction_site.city].filter(Boolean).join(' '),
                    purchaseOrder.construction_site.project_manager ? (
                      <InfoMuted>
                        Projectbeheerder:{' '}
                        {purchaseOrder.construction_site.project_manager.first_name}{' '}
                        {purchaseOrder.construction_site.project_manager.last_name}
                        {purchaseOrder.construction_site.project_manager.mobile
                          ? ` · ${purchaseOrder.construction_site.project_manager.mobile}`
                          : ''}
                      </InfoMuted>
                    ) : null,
                  ]}
                />
              ) : null}
            <SiteRemarksCallout text={purchaseOrder?.construction_site?.comments} />
          </SectionCard>
        </CardGrid>
        {purchaseOrder &&
          purchaseOrder.line_items &&
          purchaseOrder.line_items.length > 0 && (
            <Stack
              styles={styleTitle}
              horizontal
              horizontalAlign='space-between'
            >
              <Stack.Item>
                <h3>Details</h3>
              </Stack.Item>
              <Stack.Item>
                <Toggle
                  inlineLabel
                  label='Volgorde wijzigen'
                  checked={isDraggable}
                  onChange={(e, checked) => setIsDraggable(!!checked)}
                  styles={{ root: { margin: 0 }, label: { fontWeight: 400 } }}
                />
              </Stack.Item>
            </Stack>
          )}
        <Stack
          horizontal
          horizontalAlign='start'
          tokens={{ childrenGap: 30 }}
          style={{ padding: '0 24px 24px 24px', width: '100%' }}
        >
          {purchaseOrder &&
            purchaseOrder.line_items &&
            purchaseOrder.line_items.length > 0 && (
              <Stack>
                <Stack
                  horizontal
                  style={{
                    width: '100%',
                    borderBottom: '1px solid rgb(229, 229, 229);',

                    textAlign: 'left',
                  }}
                >
                  <Stack.Item
                    style={{
                      textAlign: 'center',
                      width: '75px',
                      fontWeight: 'bold',
                    }}
                  >
                    Regel
                  </Stack.Item>
                  <Stack.Item
                    style={{
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      width: '295px',
                      fontWeight: 'bold',
                    }}
                  >
                    Omschrijving
                  </Stack.Item>
                  <Stack.Item
                    style={{
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      width: '90px',
                      fontWeight: 'bold',
                    }}
                  >
                    Aantal
                  </Stack.Item>
                  <Stack.Item
                    style={{
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      width: '90px',
                      fontWeight: 'bold',
                    }}
                  >
                    Eenheid
                  </Stack.Item>
                  <Stack.Item
                    style={{
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      width: '90px',
                      fontWeight: 'bold',
                    }}
                  >
                    Prijs
                  </Stack.Item>
                  <Stack.Item
                    grow={1}
                    style={{
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      fontWeight: 'bold',
                      width: '180px',
                    }}
                  >
                    Opmerking
                  </Stack.Item>

                  <Stack.Item style={{ textAlign: 'center', width: '75px' }} />
                </Stack>
                <DraggableList
                  draggable={isDraggable}
                  onDrop={(source, target) => {
                    orderLineItems(source, target);
                  }}
                >
                  {purchaseOrder &&
                    purchaseOrder.line_items.length > 0 &&
                    purchaseOrder.line_items.map(
                      (item: IPurchaseOrderLine, index: number) => {
                        const fieldsPerRow = 5;
                        const currentFieldIndex = index * fieldsPerRow + 1;
                        return (
                          <Stack
                            horizontal
                            style={{
                              width: '100%',
                              marginTop: '20px',
                              paddingTop: '5px',
                              paddingBottom: '5px',
                              // '&:hover': { background: 'rgb(239, 239, 239)' },
                            }}
                            styles={{
                              root: {
                                paddingTop: '5px',
                                paddingBottom: '5px',
                                '&:hover': {
                                  background: 'rgb(239, 239, 239)',
                                },
                              },
                            }}
                            key={`${item.id}_${index}`}
                          >
                            <Stack.Item
                              style={{
                                textAlign: 'center',
                                width: '75px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <span>{item.weight}</span>
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                width: '350px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <InlineEditableLabel
                                isMultiline
                                isEdit
                                value={item.name || ''}
                                callBack={(value: string) =>
                                  updateItemValue(item, 'name', value)
                                }
                                tabIndex={currentFieldIndex + 1}
                              />
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                width: '100px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <InlineEditableLabel
                                isEdit
                                value={item.quantity || ''}
                                type='text'
                                callBack={(value: string) =>
                                  updateItemValue(item, 'quantity', value)
                                }
                                tabIndex={currentFieldIndex + 2}
                                noScroll
                              />
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                width: '100px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <Dropdown
                                selectedKey={
                                  item && item.measuring_unit
                                    ? item.measuring_unit.id
                                    : undefined
                                }
                                options={
                                  measuringUnits
                                    ? convertMeasuringUnitsToComboBoxOptions(
                                        measuringUnits,
                                      )
                                    : []
                                }
                                onChange={(
                                  event: React.FormEvent<HTMLDivElement>,
                                  newUnit?: IDropdownOption,
                                ) => {
                                  updateItemMeasuringUnit(item, newUnit);
                                }}
                                tabIndex={currentFieldIndex + 3}
                              />
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                width: '100px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <InlineEditableLabel
                                isEdit
                                type='text'
                                value={item.unit_price || undefined}
                                prefix='€'
                                callBack={(value: string) =>
                                  updateItemValue(item, 'unit_price', value)
                                }
                                tabIndex={currentFieldIndex + 4}
                                noScroll
                              />
                            </Stack.Item>
                            <Stack.Item
                              grow={1}
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <InlineEditableLabel
                                isMultiline
                                isEdit
                                value={item.comment || undefined}
                                callBack={(value: string) =>
                                  updateItemValue(item, 'comment', value)
                                }
                                tabIndex={currentFieldIndex + 5}
                              />
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                paddingLeft: '5px',
                                paddingRight: '5px',
                                width: '100px',
                                textAlign: 'center',
                                opacity: item.delete ? 0.5 : 1,
                              }}
                            >
                              <Icon
                                iconName={item.highlighted ? 'Highlight' : 'Highlight'}
                                styles={{
                                  root: {
                                    fontSize: '20px',
                                    color: item.highlighted ? '#28a745' : '#000000',
                                    cursor: 'pointer',
                                    padding: '5px',
                                    borderRadius: '3px',
                                    '&:hover': {
                                      backgroundColor: '#f0f0f0',
                                    },
                                  },
                                }}
                                onClick={() =>
                                  updateItemHighlighted(item, !item.highlighted)
                                }
                              />
                            </Stack.Item>
                            <Stack.Item
                              style={{
                                textAlign: 'center',
                                width: '75px',
                                opacity: 1,
                              }}
                            >
                              <Icon
                                iconName='Delete'
                                styles={iconLastProps}
                                onClick={() => {
                                  toggleDeleteItem(item.id);
                                }}
                              />
                            </Stack.Item>
                          </Stack>
                        );
                      },
                    )}
                </DraggableList>
              </Stack>
            )}
        </Stack>

        {isMailPanelOpen && (
          <MailDetail
            key={purchaseOrder?.id}
            isOpen={isMailPanelOpen}
            dismissPanel={() => {
              setSelectedFiles([]);
              setIsMailPanelOpen(false);
            }}
            mail={mail}
            setMail={setMail}
            saveMail={saveMail}
            purchaseOrder={purchaseOrder}
            constructionSite={purchaseOrder?.construction_site}
            pdfCallback={async () => {
              const { data } = await getPurchaseOrder({
                variables: {
                  where: {
                    id: purchaseOrder?.id,
                  },
                },
              });

              if (
                data?.findOnePurchaseOrder &&
                data?.findOnePurchaseOrder?.purchase_order_no &&
                data?.findOnePurchaseOrder?.construction_site?.name
              ) {
                const blob = await pdf(
                  <PurchaseOrderDownloadTemplate
                    purchaseOrder={data.findOnePurchaseOrder}
                  />,
                ).toBlob();
                const file = new File(
                  [blob],
                  `${normalizeFileNameBestelbon(
                    `Bestelbon-${
                      data?.findOnePurchaseOrder?.purchase_order_no || ''
                    }-${
                      data?.findOnePurchaseOrder?.construction_site?.name || ''
                    }`,
                  )}.pdf`,
                );

                return file;
              }
            }}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
          />
        )}
        <PurchaseOrderFooter
          purchaseOrder={purchaseOrder}
          setPurchaseOrder={setPurchaseOrder}
          constructionSite={purchaseOrder?.construction_site}
          suppliers={suppliers}
          setSupplierFilter={setSupplierSearch}
        />

        {purchaseOrder?.id ? (
          <Stack>
            <Stack.Item styles={styleTitle}>
              <h3>Mails</h3>
            </Stack.Item>
            <Stack.Item style={{ padding: '0 24px' }}>
              <CommandBar
                items={[
                  {
                    key: 'new',
                    text: 'Nieuw',
                    iconProps: { iconName: 'Add' },
                    onClick: () => {
                      openMailDetail(true).catch(() => undefined);
                    },
                    theme: commandBarTheme,
                  },
                  {
                    key: 'modify',
                    text: 'Bekijk',
                    iconProps: { iconName: 'Edit' },
                    onClick: () => {
                      openMailDetail().catch(() => undefined);
                    },
                    theme: commandBarTheme,
                    disabled: !selectionMailDetails,
                  },
                ]}
                theme={commandBarTheme}
              />
              <ShimmeredDetailsList
                items={purchaseOrder?.mail_logs || []}
                columns={[
                  {
                    key: 'column1',
                    name: 'Van',
                    fieldName: 'from',
                    minWidth: 100,
                    maxWidth: 150,
                    isRowHeader: true,
                    onRender: (log: IMailLog) => <span>{log.from}</span>,
                    data: 'string',
                    isPadded: true,
                  },
                  {
                    key: 'column2',
                    name: 'Onderwerp',
                    fieldName: 'subject',
                    minWidth: 100,
                    maxWidth: 200,
                    isRowHeader: true,
                    onRender: (log: IMailLog) => <span>{log.subject}</span>,
                    data: 'string',
                    isPadded: true,
                  },
                  {
                    key: 'column3',
                    name: 'Ontvangers',
                    fieldName: 'to',
                    minWidth: 100,
                    maxWidth: 300,
                    isRowHeader: true,
                    onRender: (log: IMailLog) => <span>{log.to}</span>,
                    data: 'string',
                    isPadded: true,
                  },
                  {
                    key: 'column0',
                    name: 'Verzonden op',
                    fieldName: 'date_created',
                    minWidth: 100,
                    maxWidth: 100,
                    isRowHeader: true,
                    onRender: (log: IMailLog) => (
                      <span>
                        {moment(log.date_created).format('DD/MM/YYYY')}
                      </span>
                    ),
                    data: 'string',
                    isPadded: true,
                  },
                ]}
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible
                selection={selectionMail}
                selectionMode={SelectionMode.single}
                selectionPreservedOnEmptyClick
                onItemInvoked={() => {
                  openMailDetail().catch(() => undefined);
                }}
              />
            </Stack.Item>
          </Stack>
        ) : (
          <Stack style={{ opacity: 0.3, pointerEvents: 'none' }}>
            <Label
              style={{
                margin: '0 24px 24px 24px',
                marginTop: '15px',
                marginBottom: '5px',
              }}
            >
              Na opslaan kan je mails versturen.
            </Label>
            <Stack.Item styles={styleTitle}>
              <h3>Mails</h3>
            </Stack.Item>
          </Stack>
        )}

        <PurchaseOrderBillitLinks
          purchaseOrderId={purchaseOrder?.id}
          isFullyLinked={purchaseOrder?.is_fully_linked}
        />

        <Stack style={{ padding: '0 24px 24px 24px', width: '100%' }}>
          <Stack.Item styles={styleTitleBijlagen}>
            <h3>Bijlagen</h3>
          </Stack.Item>
          <AttachmentList
            title='Bestelbon bijlagen'
            endpoint={
              purchaseOrder?.id
                ? `/purchase-orders/${purchaseOrder.id}/attachments`
                : undefined
            }
            blobPrefix={
              purchaseOrder?.id
                ? `purchase-orders/${purchaseOrder.id}`
                : 'purchase-orders'
            }
          />
        </Stack>

        <div className={modalContentStyles.footer}>
          <Stack
            style={{
              flexDirection: 'row',
              marginTop: 10,
              justifyContent: 'space-between',
            }}
          >
            <Stack style={{ flexDirection: 'row' }}>
              <SaveButtonWithPermissions
                disabled={
                  !purchaseOrder ||
                  !purchaseOrder.supplier ||
                  !purchaseOrder.supplier.id
                }
                save={savePurchaseOrder}
                permission='write:purchaseOrders'
              />

              <StackItem>
                <DefaultButton onClick={dismissPanel}>Annuleren</DefaultButton>
              </StackItem>
            </Stack>
            <Stack>
              {purchaseOrder?.id && downloadPDF !== undefined && (
                <PrimaryButton
                  onClick={async () => {
                    await savePurchaseOrder();
                    downloadPDF(purchaseOrder.id);
                  }}
                >
                  Download PDF <Icon iconName='PDF' />
                </PrimaryButton>
              )}
            </Stack>
          </Stack>
        </div>
      </div>
    </Panel>
  );
};

export default PurchaseOrderDetail;
